<template>
    <div>
        <b-row>
            <b-col cols="12">
                <b-alert show variant="info">
                    {{ $t('bisep_file_upload_info') }}
                </b-alert>
            </b-col>
        </b-row>

        <b-row>
            <b-col cols="12">
                <ValidationObserver ref="documentFormValidate">
                    <b-table
                        :items="documentType"
                        :fields="documentFields"
                        bordered
                        responsive
                        show-empty
                        :empty-text="$t('no_result')">

                        <template #cell(document_type)="row">
                            {{ getLocaleField(row.item.name) }}
                            <b-badge v-if="row.item.code !== 'other_document'" variant="danger" class="ml-1">
                                {{ $t('required') }}
                            </b-badge>
                        </template>


                        <template #cell(file)="row">
                        <ValidationProvider
                            :name="row.item.code"
                            :rules="isUploaded(row.item.code) ? '' : 'required'"
                            v-slot="{errors}">
                            <input type="hidden" v-model="localFormData.documents[row.item.code]" />
                            <div
                                class="file-upload-area"
                                :class="{ 'is-invalid': errors[0], 'has-file': (localFormData.documents && localFormData.documents[row.item.code]) || isUploaded(row.item.code) }"
                                @click="!isReadOnly && !isUploaded(row.item.code) && triggerFileInput(row.item.code)">

                                <div class="upload-icon">
                                    <i :class="(localFormData.documents && localFormData.documents[row.item.code]) || isUploaded(row.item.code) ? 'ri-file-text-line' : 'ri-upload-cloud-2-line'"></i>
                                </div>

                                <div class="upload-text">
                                    <div v-if="isUploaded(row.item.code) && !localFormData.documents[row.item.code]" class="file-info uploaded">
                                        <span class="file-name text-success">{{ $t('already_uploaded') }}</span>
                                        <div class="d-flex align-items-center mt-1">
                                            <b-button variant="link" size="sm" @click.native.stop="downloadFile(row.item.code)" class="p-0 text-primary mr-2">
                                                <i class="ri-download-2-line"></i> {{ $t('download') }}
                                            </b-button>
                                        </div>
                                    </div>
                                    <div v-else-if="localFormData.documents && localFormData.documents[row.item.code]" class="file-info">
                                        <span class="file-name text-primary font-weight-bold">{{ $t('new_file_selected') }}</span>
                                        <span class="small text-muted">{{ localFormData.documents[row.item.code].name }}</span>
                                        <span class="text-info small mt-1"><i class="ri-information-line"></i> {{ $t('click_save_to_upload') }}</span>
                                    </div>
                                    <div v-else-if="isRejected(row.item.code)" class="file-info rejected">
                                        <b-badge variant="danger" class="mb-1">{{ $t('rejected') }}</b-badge>
                                        <span class="text-danger small d-block mb-2">{{ $t('document_rejected_reupload_info') }}</span>
                                        <span class="placeholder-text">
                                            {{ getLocaleField(row.item.name) }} {{ $t('click_to_reupload') }}
                                        </span>
                                    </div>
                                    <span v-else class="placeholder-text">
                                        {{ getLocaleField(row.item.name) }} {{ $t('click_to_select') }}
                                    </span>
                                </div>
                                <input
                                    type="file"
                                    :ref="`fileInput_${row.item.code}`"
                                    style="display: none"
                                    @change="handleFileChange($event, row.item.code)"
                                    :accept="'.pdf,.doc,.docx'"
                                />
                            </div>
                            <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]"/>
                        </ValidationProvider>
                    </template>
                        <template #cell(sample_document)="row">
                            <b-button
                                variant="success"
                                size="sm"
                                @click="DownloadSampleDocument(row.item)"
                                v-if="row.item.code !== 'other_document'">
                                {{ $t('sample_document') }}
                            </b-button>
                        </template>

                        <template #cell(actions)="row">
                            <div class="d-flex align-items-center">
                                <b-button
                                    variant="primary"
                                    size="sm"
                                    :disabled="!localFormData.documents || !localFormData.documents[row.item.code]"
                                    @click="uploadFile(row.item.code, localFormData.documents[row.item.code])">
                                    {{ $t('save') }}
                                </b-button>
                                <b-button
                                    v-if="isUploaded(row.item.code)"
                                    variant="danger"
                                    size="sm"
                                    class="ml-2"
                                    @click="onDeleteDocument(row.item.code)">
                                    <i class="ri-delete-bin-line"></i>
                                </b-button>
                            </div>
                        </template>
                    </b-table>
                </ValidationObserver>
            </b-col>
        </b-row>

        <b-row>
            <b-col cols="12">
                <b-alert variant="info" show>{{ footerInfo }}</b-alert>
            </b-col>
        </b-row>

        <div class="d-flex justify-content-center mt-3" v-if="!isReadOnly && !showOnlyRejected">
            <processing-button
                :processing="loading"
                :disabled="!isAllDocumentsUploaded"
                variant="primary"
                :label="applicationButtonTitle"
                @click="saveDocuments"
            />
        </div>
    </div>
</template>

<script>
import ProcessingButton from '@/components/elements/ProcessingButton'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import ConstantService from "@/services/ConstantService";
import BisepService from "@/services/BisepService"

export default {
    name: "DocumentsTab",
    components: {
        ProcessingButton,
        ValidationObserver,
        ValidationProvider
    },
    props: {
        footerInfo: {
            type: String,
            default: ''
        },
        isReadOnly: {
            type: Boolean,
            default: false
        },
        showOnlyRejected: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            loading: false,
            formData: {},
            localFormData: {
                documents: {}
            },
            documentFields: [
                { key: 'document_type', label: this.$t('document_type') },
                { key: 'file', label: this.$t('file') },
                { key: 'sample_document', label: this.$t('sample_document') },
                { key: 'actions', label: this.$t('action') }
            ]
        }
    },
    computed: {
        documentType() {
            const docs = this.$store.getters.getParameter('bisep_applications.documents') || [];
            if (this.showOnlyRejected) {
                return docs.filter(doc => this.isRejected(doc.code));
            }
            return docs;
        },
        isAllDocumentsUploaded() {
            if (!this.documentType) return false;
            return this.documentType.every(doc => {
                if (doc.code === 'other_document') return true;

                return this.isUploaded(doc.code) && !this.isRejected(doc.code);
            });
        },
        applicationButtonTitle() {
            return this.formData.status === 'waiting_approve'
                ? this.$t('save').toUpper()
                : this.$t('complete_application').toUpper()
        },
        hasAnyRejectedLeft() {
            if (!this.formData || !this.formData.documents) return false;
            return Object.values(this.formData.documents).some(status => status === 'declined');
        }
    },
    async mounted() {
        this.loadFormData()
        await this.$store.dispatch("fetchParameter", {
            parameterCode: "bisep_applications.documents",
            isPublic: true,
            sort: '-rank'
        });
        if (this.isReadOnly) {
            this.documentFields = this.documentFields.filter(f => f.key !== 'actions');
        }
        this.setDocuments();
    },
    methods: {
        loadFormData() {
            const data = this.getWithExpiry('bisep_app_data', true)
            if (data) {
                this.formData = data
                if (!this.formData.documents) {
                    this.formData.documents = {}
                }
            }
        },
        getPin() {
            return this.$store.getters['bisepApplication/getPin']
        },
        isUploaded(code) {
            return this.formData.documents && this.formData.documents[code] === 'uploaded';
        },
        isRejected(code) {
            return this.formData.documents && this.formData.documents[code] === 'declined';
        },

        triggerFileInput(code) {
            const refName = `fileInput_${code}`
            const input = this.$refs[refName]
            if (input) {
                if (Array.isArray(input)) {
                    input[0].click()
                } else {
                    input.click()
                }
            }
        },
        handleFileChange(event, code) {
            const file = event.target.files[0]
            if (file) {
                this.$set(this.localFormData.documents, code, file)
            }
        },
        getLocaleField(field) {
            if (typeof field === 'object' && field !== null) {
                return field[this.$i18n.locale] || field.tr || field.en || field
            }
            return field
        },
        setDocuments() {
            if (!this.documentType) return
            this.documentType.forEach(slotStatus => {
                this.$set(this.localFormData.documents, slotStatus.code, null)
            })
        },
        async saveDocuments() {
            if (this.isAllDocumentsUploaded) {
                this.loading = true
                const data = {
                    national_id: this.formData.national_id,
                    pin: this.getPin()
                }
                BisepService.sendApprove(data).then(response => {
                    this.$toast.success(this.$t('api.'+response.data.message))
                    this.$emit('success')
                }).catch(e => {
                    this.showErrors(e, null)
                }).finally(() => {
                    this.loading = false
                })
            } else {
                this.$toast.warning(this.$t('please_upload_required_documents_error'))
            }
        },
        uploadFile(type, file) {
            this.loading = true
            const formData = new FormData()
            formData.append('national_id', this.formData.national_id)
            formData.append('pin', this.getPin())
            formData.append('type', type)
            formData.append('file', file)

            BisepService.addFile(formData).then((response) => {
                this.$toast.success(this.$t('proccess_success'))
                const updatedData = response.data.data
                this.formData = { ...this.formData, ...updatedData }
                this.$store.commit('bisepApplication/setData', { data: this.formData })
                this.$set(this.localFormData.documents, type, null)

                // Eğer revizyon modundaysak ve başka reddedilen belge kalmadıysa başarı olayını tetikle
                if (this.showOnlyRejected && !this.hasAnyRejectedLeft) {
                    this.$emit('success');
                }
            }).catch(e => {
                this.showErrors(e, null)
            }).finally(() => {
                this.loading = false
            })
        },
        onDeleteDocument(type) {
            this.$swal.fire({
                text: this.$t('are_you_sure_to_delete'),
                showCancelButton: true,
                confirmButtonText: this.$t('yes'),
                cancelButtonText: this.$t('no'),
            }).then((result) => {
                if (result.isConfirmed) {
                    const data = {
                        national_id: this.formData.national_id,
                        pin: this.getPin(),
                        type: type
                    }
                    BisepService.deleteFile(data).then((response) => {
                        this.$toast.success(this.$t('proccess_success'))
                        const updatedData = response.data.data
                        this.formData = { ...this.formData, ...updatedData }
                        this.$store.commit('bisepApplication/setData', { data: this.formData })
                        this.$set(this.localFormData.documents, type, null)

                    }).catch(e => {
                        this.showErrors(e, null)
                    })
                }
            })
        },
        downloadFile(type) {
            const data = {
                national_id: this.formData.national_id,
                pin: this.getPin(),
                type: type
            }
            BisepService.downloadFile(data).then(response => {

                this._downloadFile(response, type+'.'+(response.headers.file_extension))
            }).catch(e => {
                this.showErrors(e, null, true)
            })
        },

        DownloadSampleDocument(documentType) {
            let key = null
            if (documentType.code === 'resume') {
                key = 'bisep_application.sample_resume'
            } else if (documentType.code === 'motivation_letter') {
                key = 'bisep_application.sample_motivation_letter'
            } else {
                return
            }
            ConstantService.downloadPublicFile(key).then(response => {
                this._downloadFile(response, documentType.name + '.' + (response.headers.file_extension))
            }).catch(e => {
                this.showErrors(e, null)
            })
        },
    }
}
</script>
<style>
.file-upload-area {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    border: 2px dashed #dee2e6;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease;
    min-height: 60px;
}

.file-upload-area:hover {
    border-color: #0d6efd;
    background-color: #f8f9fa;
}

.file-upload-area.is-invalid {
    border-color: #dc3545;
}

.file-upload-area.has-file {
    border-color: #198754;
    border-style: solid;
    background-color: #f0fff4;
}

.upload-icon {
    font-size: 24px;
    color: #6c757d;
    min-width: 32px;
}

.file-upload-area:hover .upload-icon {
    color: #0d6efd;
}

.file-upload-area.has-file .upload-icon {
    color: #198754;
}

.upload-text {
    flex: 1;
    color: #6c757d;
    font-size: 14px;
}

.placeholder-text {
    color: #6c757d;
}

.file-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.file-name {
    color: #198754;
    font-weight: 600;
    font-size: 14px;
}

.file-size {
    color: #6c757d;
    font-size: 12px;
}
</style>
